import React, { createContext, FC, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import lazyBlock from '../../../tools/lazyBlock';
import { arePanelTranslationsReady } from '../../../tools/panelTranslationsReady';
import { shouldHoldPanelOpen } from '../../../tools/panelOpenHold';
import { useClusterContext } from '../../Routes/Workspace/Cluster/ClusterContext';
import { ConfirmPopup } from '../ConfirmPopup/ConfirmPopup';
import { ProxyHistoryParams } from '../../../entities/AssetManagement/global';
import { IState } from '../../../reducers';
import { ILanguageReducer } from '../../../reducers/languageReducer';
import IntlMessage from '../IntlMessage';

export enum EPanelType {
    AM = 'AM',
    QA = 'QA',
    BULK = 'Bulk',
    SL = 'SL',
    FAQ = 'FAQ',
    CLAUSIER = 'Clausier',
    CLAUSIERPOA = 'ClausierPoa',
    CLAUSIERPOACLUSTER = 'ClausierPoaCluster',
    APPROVAL = 'Approval',
    CLUSTER = 'Cluster',
    TALK = 'Talk',
    GROUP = 'Group',
    WORKSPACE = 'Workspace',
    LABELS = 'LABELS',
    CATEGORIES = 'CATEGORIES',
    LIMITS = 'LIMITS',
    PERIODS = 'PERIODS',
    KEYDATES = 'KEY_DATES',
    FINAL_DECISION = 'FINAL_DECISION',
    CREATE_DELEGATION = 'CREATE_DELEGATION',
    WORKSPACE_INVITATION = 'WORKSPACE_INVITATION',
    INVITATION_RULE = 'INVITATION_RULE',
    /** Legal-entity TPRM screening (cluster panel) */
    LETPRM = 'LETPRM',
    /** Individual TPRM screening (cluster panel; LETPRM-compatible payload). */
    IndividualnScreening = 'IndividualnScreening',
    /** Personal TPRM screening (separate panel implementation) */
    PersonalTPRM = 'PersonalTPRM',
    TPRM_DOCUMENT_REVIEW = 'TPRM_DOCUMENT_REVIEW',
    SIGNATURE_STATUS = 'SignatureStatus',
    /** TPRM template signature — same approval wizard as popup, embedded in panel. */
    SignatureTPRM = 'SignatureTPRM',
    /** TPRM final score (cluster resource action panel). */
    FinalScore = 'FinalScoreTPRM',
    /** Copy / transfer user permissions from User Profile. */
    MANAGE_PERMISSIONS = 'MANAGE_PERMISSIONS',
    /** Entity Monitoring (MemberCheck) — cluster resource action panel. */
    ENTITY_MONITORING = 'EntityMonitoring'
}

export enum EPanelUrlParams {
    PANELTYPE = 'panelType',
    COMMENTS = 'comments',
    THREADID = 'threadId',
    MESSAGEID = 'messageId',
    CLUSTERID = 'clusterId',
    ORGANIZATIONSID = 'organizationsId',
    GROUPID = 'groupId',
    MAINIDS = 'mainIds',
    SEARCHFAQ = 'searchFAQ',
    SEARCHCLAUSES = 'searchClauses',
    SEARCHPOACLAUSES = 'searchPoaClauses',
    CREATEIFNOTEXISTS = 'createIfNotExists',
    PREVIOUSPANELTYPE = 'previousPanelType',
    EDITMODE = 'editMode',
    EDITPRESERVEBACKGROUND = 'edit',
    REFRESH = 'refresh',
    VALIDATIONID = 'validationId',
    LABEL = 'label',
    NAVIGATING = 'navigating',
    ORGCONTEXT = 'orgContext',
    KEYDATEID = 'keyDateId',
    SUBSTITUTIONID = 'substitutionId',
    PERMISSIONS_MODE = 'permissionsMode',
    PERMISSIONS_RESOURCE_IDS = 'permissionsResourceIds',
    PERMISSIONS_RESOURCES = 'permissionsResources',
    PERMISSIONS_WORKSPACE_ID = 'permissionsWorkspaceId',
    /** Cluster Actions row title for panels opened from a specific action (e.g. TPRM document review). */
    RESOURCE_ACTION_NAME = 'resourceActionName',
    /** Cluster Actions row id; used to load/send the discussion thread bound to a resource action. */
    RESOURCE_ACTION_ID = 'ResourceActionId',
    FILEID = 'fileId',
    IS_DATAGRID = 'isDatagrid'
}

export interface IMainPanelContext {
    showPanel: boolean;
    showComments: boolean;
    editorOpened: string[];
    /** When true, slide-out panel hides header title area and body (join / permission ConfirmPopup is shown). */
    joinPopupVisible?: boolean;
    setShowComments(state: boolean);
    setShowPanel(state: boolean);
    setEditorOpened: React.Dispatch<React.SetStateAction<string[]>>;
    setJoinPopupVisible?(state: boolean);
    historyWithWarnings(historyParams: ProxyHistoryParams);
}

const ContentSwitcher = {
    [EPanelType.TALK]: lazyBlock(lazy(() => import('./PanelTypes/Talk'))),
    [EPanelType.APPROVAL]: lazyBlock(lazy(() => import('./PanelTypes/Approval'))),
    [EPanelType.CLUSTER]: lazyBlock(lazy(() => import('./PanelTypes/Cluster'))),
    [EPanelType.AM]: lazyBlock(lazy(() => import('./PanelTypes/AM'))),
    [EPanelType.GROUP]: lazyBlock(lazy(() => import('./PanelTypes/Group'))),
    [EPanelType.WORKSPACE]: lazyBlock(lazy(() => import('./PanelTypes/Workspace'))),
    [EPanelType.SL]: lazyBlock(lazy(() => import('./PanelTypes/SL'))),
    [EPanelType.FAQ]: lazyBlock(lazy(() => import('./PanelTypes/FAQ'))),
    [EPanelType.CLAUSIER]: lazyBlock(lazy(() => import('./PanelTypes/Clausier'))),
    [EPanelType.BULK]: lazyBlock(lazy(() => import('./PanelTypes/Bulk'))),
    [EPanelType.CLAUSIERPOA]: lazyBlock(lazy(() => import('./PanelTypes/ClausierPOA'))),
    [EPanelType.CLAUSIERPOACLUSTER]: lazyBlock(lazy(() => import('./PanelTypes/ClausierPOACluster'))),
    [EPanelType.QA]: lazyBlock(lazy(() => import('./PanelTypes/QA'))),
    [EPanelType.LABELS]: lazyBlock(lazy(() => import('./PanelTypes/Labels'))),
    [EPanelType.CATEGORIES]: lazyBlock(lazy(() => import('./PanelTypes/Categories'))),
    [EPanelType.LIMITS]: lazyBlock(lazy(() => import('./PanelTypes/Limits'))),
    [EPanelType.PERIODS]: lazyBlock(lazy(() => import('./PanelTypes/Periods'))),
    [EPanelType.KEYDATES]: lazyBlock(lazy(() => import('./PanelTypes/KeyDates'))),
    [EPanelType.FINAL_DECISION]: lazyBlock(lazy(() => import('./PanelTypes/FinalDecision'))),
    [EPanelType.CREATE_DELEGATION]: lazyBlock(lazy(() => import('./PanelTypes/CreateDelegation'))),
    [EPanelType.WORKSPACE_INVITATION]: lazyBlock(lazy(() => import('./PanelTypes/WorkspaceInvitation'))),
    [EPanelType.INVITATION_RULE]: lazyBlock(lazy(() => import('./PanelTypes/InvitationRule'))),
    [EPanelType.LETPRM]: lazyBlock(lazy(() => import('./PanelTypes/ScreeningLETPRM'))),
    [EPanelType.IndividualnScreening]: lazyBlock(lazy(() => import('./PanelTypes/IndividualnScreening'))),
    [EPanelType.PersonalTPRM]: lazyBlock(lazy(() => import('./PanelTypes/ScreeningPersonal'))),
    [EPanelType.TPRM_DOCUMENT_REVIEW]: lazyBlock(lazy(() => import('./PanelTypes/TprmDocumentReview'))),
    [EPanelType.SIGNATURE_STATUS]: lazyBlock(lazy(() => import('./PanelTypes/SignatureStatusPanel'))),
    [EPanelType.SignatureTPRM]: lazyBlock(lazy(() => import('./PanelTypes/ScreeningSignature'))),
    [EPanelType.FinalScore]: lazyBlock(lazy(() => import('./PanelTypes/FinalScore'))),
    [EPanelType.MANAGE_PERMISSIONS]: lazyBlock(lazy(() => import('./PanelTypes/ManagePermissions'))),
    [EPanelType.ENTITY_MONITORING]: lazyBlock(lazy(() => import('./PanelTypes/EntityMonitoring')))
};

export const MainPanelContext = createContext<Partial<IMainPanelContext>>(undefined);

/**
 * When the cluster join / permission ConfirmPopup is open, call this so {@link Panel} hides header and body.
 * Render the ConfirmPopup as a sibling of Panel (not inside Panel children), otherwise it would be hidden too.
 */
export function useNotifyMainPanelJoinPopup(panelContext: Partial<IMainPanelContext>, visible: boolean): void {
    useEffect(() => {
        panelContext.setJoinPopupVisible?.(visible);
        return () => panelContext.setJoinPopupVisible?.(false);
    }, [visible, panelContext.setJoinPopupVisible]);
}

export const PanelContainer: FC = () => {
    const history = useNavigate();
    const { search, pathname } = useLocation();
    const clusterContext = useClusterContext();
    const { translations, userLanguage } = useSelector<IState, ILanguageReducer>(state => state.language);
    const [showPanel, setShowPanel] = useState<boolean>(false);
    const [showComments, setShowComments] = useState<boolean>(false);
    const [editorOpened, setEditorOpened] = useState<string[]>([]);
    const [joinPopupVisible, setJoinPopupVisible] = useState<boolean>(false);
    const [panelType, setPanelType] = useState<EPanelType>(undefined);
    const [showLeavePanelConfirmPopup, setShowLeavePanelConfirmPopup] = useState<boolean>(false);
    const [historyParamsAfterConfirmPopup, setHistoryParamsAfterConfirmPopup] = useState<ProxyHistoryParams>(undefined);

    let Panel = ContentSwitcher[panelType];

    const params = useMemo(() => new URLSearchParams(search), [search]);

    useEffect(() => {
        const bodyObject = document.getElementsByTagName('body')[0];
        if (showPanel) {
            bodyObject.style.overflow = 'hidden';
        } else {
            bodyObject.style.overflow = 'auto';
        }
    }, [showPanel]);

    // Define a proxy for intercepting history changes to ensure that when we leave the page some behaviours can be added
    // it triggers a confirm popup to show when leaving with some editor opened
    const historyWithWarnings = useCallback((historyParams: ProxyHistoryParams) => {
        if (editorOpened.length > 0) {
            setShowLeavePanelConfirmPopup(true);
            setHistoryParamsAfterConfirmPopup(historyParams);
        } else {
            if (historyParams.doneCallback) {
                historyParams.doneCallback();
            }

            history(historyParams);
        }
    }, [editorOpened, setShowLeavePanelConfirmPopup, setHistoryParamsAfterConfirmPopup]);

    const applyHistoryParams = useCallback((_historyParams?: ProxyHistoryParams) => {
        if (historyParamsAfterConfirmPopup) {
            if (historyParamsAfterConfirmPopup?.doneCallback) {
                historyParamsAfterConfirmPopup?.doneCallback();
            }

            history(historyParamsAfterConfirmPopup);
            setHistoryParamsAfterConfirmPopup(undefined);
        } else {
            console.log('No params to set history, skipping navigation'); // please leave for debugging purpose
        }
    }, [historyParamsAfterConfirmPopup, setHistoryParamsAfterConfirmPopup]);

    const initState: IMainPanelContext = useMemo(() => ({
        showPanel,
        showComments,
        editorOpened,
        joinPopupVisible,
        setShowComments,
        setShowPanel,
        setEditorOpened,
        setJoinPopupVisible,
        historyWithWarnings
    }), [showPanel, showComments, editorOpened, joinPopupVisible, setShowComments, setShowPanel, setEditorOpened, historyWithWarnings]);

    useLayoutEffect(() => {
        const panelTypeParam = params.get(EPanelUrlParams.PANELTYPE);
        const commentsParam = params.get(EPanelUrlParams.COMMENTS);
        const holdPanel = shouldHoldPanelOpen({
            pathname,
            panelType: panelTypeParam,
            loadedClusterId: clusterContext.currentCluster?.id,
            translationsReady: arePanelTranslationsReady(translations as Record<string, unknown>, userLanguage),
            deferPanelFromUrl: clusterContext.deferPanelFromUrl
        });

        if (holdPanel) {
            setShowPanel(false);
            setShowComments(false);
            setPanelType(undefined);
            return;
        }

        if (Object.values(EPanelType).includes(panelTypeParam as EPanelType)) {
            setPanelType(panelTypeParam as EPanelType);
            setShowComments(commentsParam === 'true');
            return;
        }

        setShowPanel(false);
        setShowComments(false);
        setPanelType(undefined);
    }, [params, pathname, clusterContext.deferPanelFromUrl, clusterContext.currentCluster?.id, translations, userLanguage]);

    useEffect(() => {
        setJoinPopupVisible(false);
    }, [panelType]);

    const leaveYesHandler = () => {
        applyHistoryParams();
        setEditorOpened([]);
        setShowLeavePanelConfirmPopup(false);
    };

    const leaveNoHeandler = () => {
        setHistoryParamsAfterConfirmPopup(undefined);
        setShowLeavePanelConfirmPopup(false);
    };

    return (
        <MainPanelContext.Provider value={initState}>
            {Panel &&
                <Panel />
            }
            <ConfirmPopup
                showPopup={showLeavePanelConfirmPopup}
                yesHandler={leaveYesHandler}
                noHandler={leaveNoHeandler}
            >
                <IntlMessage id="panel.confirm.unsavedChanges" />
            </ConfirmPopup>
        </MainPanelContext.Provider>
    );
};
